import { cn } from 'noddi-ui-common';
import { type CarProps, formatCarLicensePlate } from 'noddi-util';
import { NavioTireSeasonChip, type NavioTireSeasonChipTranslations } from '../../CarWheel/NavioTireSeasonChip';
import type { TireSeasonInput } from '../../CarWheel/tireSeasonStyle';

type Size = 'sm' | 'md';

// sm in table cells and list rows, md as the plate a card or sheet is about. The season box matches the plate's
// line height so the two align on center.
const PLATE_SIZE = {
  sm: { text: 'text-sm leading-[22px] tracking-[0.06em]', gap: 'gap-2' },
  md: { text: 'text-[22px] leading-7 tracking-[0.01em]', gap: 'gap-2.5' }
} as const satisfies Record<Size, { text: string; gap: string }>;

interface Props {
  car: CarProps;
  /**
   * The season box in front of the plate. `null` (no or unknown season) keeps an empty box so plates in a column stay
   * aligned; leave it out where a row has no season at all.
   */
  season?: TireSeasonInput | null;
  size?: Size;
  /**
   * `plate`: bold, no outline. `badge`: outlined, for several plates side by side in one line.
   * `text`: inherits the surrounding text.
   */
  variant?: 'plate' | 'badge' | 'text';
  className?: string;
  /** The season box's accessible name; the customer app passes its translated labels. */
  seasonTranslations?: NavioTireSeasonChipTranslations;
}

export const NavioLicensePlate = ({
  car,
  season,
  size = 'sm',
  variant = 'plate',
  className,
  seasonTranslations
}: Props) => {
  const plateText = formatCarLicensePlate(car);
  const seasonBox =
    season === undefined ? null : (
      <NavioTireSeasonChip season={season} size={size} iconOnly placeholder translations={seasonTranslations} />
    );

  if (!plateText) {
    return seasonBox;
  }

  return (
    <span
      className={cn(
        'whitespace-nowrap tabular-nums',
        variant === 'plate' && cn('inline-flex items-center font-bold', PLATE_SIZE[size].text, PLATE_SIZE[size].gap),
        variant === 'badge' &&
          'inline-flex items-center justify-center rounded-md border border-border bg-background px-2 py-0.5 text-sm font-semibold tracking-wider',
        className
      )}
    >
      {seasonBox}
      {plateText}
    </span>
  );
};
